import { CurrencyCollectionQuery, OrderKindQuery, PagedCollectionQuery, PagedCollectionQueryResult } from '@skylab-shared/business';
import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { OrderDocumentKind, OrderPaymentState, OrderPaymentType, OrderShipmentMethodKind, OrderSourceKind, OrderStatus, SaleDocumentKind } from '@skylab-shared/dataModel';
import { fillPlaceholders } from '@skylab-shared/utils';

export class OrderGridItem {
	recipient: string;
	currency: {
		externalId: string;
		precision: number;
		symbol: string;
	};
	totalGrossValue: number;
	totalNetValue: number;
	id: string;
	orderDate: Date;
	dueDate: Date;
	dispatchEnd: Date;
	orderNumber: string | null;
	shipmentMethodName: string;
	commission: number;
	source: OrderSourceKind;
	notesPrivate: string | null;
	notesPublic: string | null;
	statusCode: OrderStatus;
	paymentType: OrderPaymentType;
	document: {
		kind: SaleDocumentKind,
		saleDocNumber: string;
		saleDocExternalId: string;
	};
	paymentState: OrderPaymentState;
}

class OrderGridItemDTO {
	orderId: string;
	number: string | null;
	storeOrderId: number;
	source_Kind: OrderSourceKind;
	source_ExternalNumber: string;
	status_InnerStatus: OrderStatus;
	orderDate: Date;
	createdDate: Date;
	modifiedDate: Date;
	dueDate: Date;
	currencyExternalId: string;
	recipient: string;
	totalGrossValue: number;
	totalNetValue: number;
	totalCommission: number;
	notesPrivate: string | null;
	notesPublic: string | null;
	shipmentMethod_Kind: OrderShipmentMethodKind;
	payment_Type: OrderPaymentType;
	paymentState: OrderPaymentState;
	document: {
		kind: SaleDocumentKind,
		salesDocNumber: string;
		salesDocExternalId: string;
	} | null;
	deliveries: {
		id: number,
		kind: OrderShipmentMethodKind,
		serviceName: string,
		serviceId: number,
		dispatchEnd: Date
	}[]
}

type OrderGridResponse = {
	data: {
		items: OrderGridItemDTO[],
		metadata: {
			page: number,
			total: number
		}
	}
}

export class OrderGridCollectionQuery extends PagedCollectionQuery<OrderGridItem> { }

export class OrderGridCollectionQueryHandlerBase implements QueryHandler<OrderGridCollectionQuery, PagedCollectionQueryResult<OrderGridItem>> {
private static Url = TaggedUrl.join('views/orders/:viewId/data', [Tags.SelloApi]);

	public constructor(
		private readonly http: HttpService,
		private readonly rootContext: RootContextBase,
	) { }

	public async handleAsync(query: OrderGridCollectionQuery): Promise<PagedCollectionQueryResult<OrderGridItem>> {
		const url = fillPlaceholders(OrderGridCollectionQueryHandlerBase.Url, { viewId: query.params?.['viewId'] });
		const response = await this.http.get<OrderGridResponse>(url, {
			params: { ...query.params },
		});

		const items = await Promise.all(response.data.data.items.map<Promise<OrderGridItem>>(async e => {
			const currency = this.rootContext.firstPreloaded(CurrencyCollectionQuery, { externalId: e.currencyExternalId });

			const item: OrderGridItem = {
				currency: {
					externalId: currency.externalId,
					symbol: currency.symbol,
					precision: currency.precision
				},
				recipient: e.recipient,
				totalGrossValue: e.totalGrossValue,
				totalNetValue: e.totalNetValue,
				id: e.orderId,
				commission: e.totalCommission,
				dueDate: e.dueDate,
				orderDate: e.orderDate,
				orderNumber: e.number,
				shipmentMethodName: e.deliveries?.[0]?.serviceName,
				dispatchEnd: e.deliveries?.[0]?.dispatchEnd,
				notesPrivate: e.notesPrivate,
				document: {
					kind: e.document?.kind || (await this.rootContext.firstAsync(OrderKindQuery, { name: OrderDocumentKind.Order })).defaultDocumentToIssue,
					saleDocExternalId: e.document?.salesDocExternalId || '',
					saleDocNumber: e.document?.salesDocNumber || ''
				},
				notesPublic: e.notesPublic,
				source: e.source_Kind,
				statusCode: e.status_InnerStatus,
				paymentType: e.payment_Type,
				paymentState: e.payment_Type === OrderPaymentType.CASH_ON_DELIVERY ? OrderPaymentState.CashOnDelivery : e.paymentState
			}
			return item;
		}))

		return {
			items,
			totalItemsCount: response.data.data.metadata.total
		}
	}
}
